import { useCallback, ReactElement, useMemo, memo } from 'react'
import { Stream, Feed } from 'interfaces'
import StreamViewNavigation from './StreamViewNavigation'
import SaveButton from 'components/SaveButton'
import { FormattedMessage } from 'react-intl'
import ArrowIcon from '@mui/icons-material/KeyboardArrowRight'
import { Link } from 'react-router'

import styles from './StreamViewHeader.pcss'

export type StreamViewHeaderProps = {
  stream: Stream
  isSaved: boolean
  saving?: boolean
  navigation: StreamViewNavigation
  showBackLink?: boolean
  hideSaveButton?: boolean
  customButton?: ReactElement<any>
  activeFeed?: Feed
  location?: {
    search?: string
    state?: any
  }
  onBackClick?(): void
  onSave?(stream: Stream): void
}

const StreamViewHeaderComponent: React.FC<StreamViewHeaderProps> = (props) => {
  const { stream, isSaved, saving, hideSaveButton, customButton, showBackLink, onBackClick, activeFeed } = props

  const handleSave = useCallback(() => {
    props.onSave?.(props.stream)
  }, [props])

  const feedBreadcrumb = useMemo(() => {
    if (!activeFeed) return null
    return (
      <>
        <ArrowIcon className={styles['arrow-icon']} />
        <h2 key="feed-name" title={activeFeed.name} className={`${styles.title} text-ellipsis`}>
          <Link to={`/content/feeds/${activeFeed.uniqueSource}`}>{activeFeed.name}</Link>
        </h2>
      </>
    )
  }, [activeFeed])

  const streamNameEl = useMemo(() => {
    const locationProps = props.location
    return (
      <h2 key="title" title={stream.title} className={`${styles.title} ${styles['stream-title']} text-ellipsis`}>
        {activeFeed ? (
          <Link
            key="stream-link"
            to={{ pathname: props.navigation.prefix, search: locationProps?.search }}
            state={locationProps?.state}
          >
            {stream.title}
          </Link>
        ) : (
          stream.title
        )}
      </h2>
    )
  }, [activeFeed, props.location, props.navigation.prefix, stream.title])

  return (
    <header className={styles.header}>
      {(!hideSaveButton && !isSaved) && (
        <SaveButton
          key="save-btn"
          saved={isSaved}
          loading={saving}
          className={styles['btn-save']}
          onClick={handleSave}
        />
      )}
      {streamNameEl}
      {feedBreadcrumb}
      {customButton || null}
      {showBackLink && (
        <span key="back" onClick={onBackClick} className={styles.navigation}>
          <FormattedMessage id="general.carousel.nav.back" defaultMessage="back" />
        </span>
      )}
    </header>
  )
}

function propsAreEqual(prev: StreamViewHeaderProps, next: StreamViewHeaderProps) {
  return prev.stream === next.stream &&
    prev.navigation === next.navigation &&
    prev.isSaved === next.isSaved &&
    prev.customButton === next.customButton &&
    prev.saving === next.saving &&
    prev.activeFeed === next.activeFeed
}

export const StreamViewHeader = memo(StreamViewHeaderComponent, propsAreEqual)
export default StreamViewHeader
